import axios from 'axios';
import { pdf } from '@react-pdf/renderer';
import { API_PATH } from 'config';
import { getAccessTokenApi } from 'api/auth';
import { getStaticMantencionData } from 'api/budget';
import { getPdfTemplateByTypeApi } from 'api/pdfTemplate';
import { resolveSignatureUrl } from 'api/signatures';
import { requestMantencion } from 'hooks/useAppApi';
import { deriveTenantColors } from 'PDF/TemplateEngine/deriveTenantColors';
import { buildInformeMantencionDoc } from './buildInformeMantencionDoc';

// Genera el informe de mantención (PDF client-side) y lo envía a
// /mantenciones/send-mantencion, cargando todos los datos a partir del id.
// Función pura, sin React state/message — la usa el daemon offline
// (flushPendingCierres), que solo tiene el id de la mantención (no datos ya
// cargados en un provider como en el flujo online). Lanza en cualquier
// fallo para que el daemon clasifique el error (reintentable vs terminal).
//
// El renderer (lowcode vs legacy) se elige vía buildInformeMantencionDoc,
// según `design_version` de la mantención — mismo criterio que la pantalla
// de descarga (InformeMantencionDownload / MantencionDowload), para que el
// PDF archivado/enviado sea idéntico al que el usuario descarga.
//
// Nota de coordinación (2026-07-05): esta lógica de render+POST está
// duplicada intencionalmente con `useMantencionSend.jsx` (hook del flujo
// online) para evitar tocar ese archivo mientras otra consola (envio-auto)
// edita concurrentemente su bloque de destinatarios. Ver
// thoughts/pendientes.md ("Unificar duplicación render+POST del informe de
// mantención") para el plan de unificación posterior.
export async function sendMantencionPdf(id) {
  const token = getAccessTokenApi();
  const [{ static: staticData, tenant: config }, { data: mantencion }, templateRaw] =
    await Promise.all([
      getStaticMantencionData(token),
      requestMantencion(id),
      getPdfTemplateByTypeApi(null, 'informe_mantencion'),
    ]);
  const template = templateRaw?.status === 'client' ? null : templateRaw || null;
  const { Ascensores, mantencion: mantencionData, actividades } = mantencion;

  // Resolver firmas a presigned URLs ANTES de generar el PDF:
  // `pdf().toBlob()` es imperativo y no espera a useEffect async dentro
  // del documento, por eso las firmas hay que resolverlas acá.
  const [firmaSpecUrl, firmaRecepUrl] = await Promise.all([
    resolveSignatureUrl(mantencionData?.firma_spec),
    resolveSignatureUrl(mantencionData?.firma_recep),
  ]);
  const mantencionWithSignatures = {
    ...mantencionData,
    firma_spec: firmaSpecUrl,
    firma_recep: firmaRecepUrl,
  };

  const doc = buildInformeMantencionDoc({
    mantencionPayload: { Ascensores, mantencion: mantencionWithSignatures, actividades },
    template,
    staticData,
    config,
    colors: deriveTenantColors(config),
    signatures: { firma_spec: firmaSpecUrl, firma_recep: firmaRecepUrl },
  });
  const asPdf = pdf([]);
  asPdf.updateContainer(doc);
  const blob = await asPdf.toBlob();

  // Envía el PDF a la API. El daemon offline no maneja destinatarios
  // explícitos, por eso to/cc van vacíos.
  const formData = new FormData();
  formData.append('file', blob, `Informe de mantencion N°${mantencionData.id}.pdf`);
  formData.append('id', mantencionData.id);
  formData.append('to', JSON.stringify([]));
  formData.append('cc', JSON.stringify([]));
  await axios.post(`${API_PATH}/mantenciones/send-mantencion`, formData, {
    headers: { Authorization: getAccessTokenApi() },
  });
}
